import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { apyToAprPercent } from '../../../utils/parsing'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Apyx apyUSD — ERC-4626 yield vault over apxUSD, the "Dividend-Backed
 * Dollar" (docs.apyx.fi): apxUSD is backed by variable-rate perpetual
 * preferred stock issued by Digital Asset Treasury companies (STRC /
 * Strategy), and the off-chain dividends are minted as apxUSD and fed
 * to the vault through a 14-day `LinearVestV0` drip
 * (`YieldDistributor.depositYield()` → `pullVestedYield()`), so the
 * share price appreciates continuously. No on-chain solvency invariant
 * — the backing sits in off-chain preferred shares (RWA/NAV trust
 * class, like Spark V2 / Maple).
 *
 * Rate source: **on-chain**, reproducing the protocol's own
 * `ApyUSDRateView.apr()` formula exactly —
 *
 *   apr = vesting.unvestedAmount() / vesting.vestingPeriodRemaining()
 *         × yearSeconds / vault.totalAssets()
 *
 * Verified 2026-08-04: 774.2k apxUSD unvested over 11.13d remaining on
 * 181.4M totalAssets → 13.99 % APR, matching DefiLlama's
 * `apyx-protocol` pool (14.00) and compounding monthly to the 14.94 %
 * APY Apyx's own API reports. The vesting contract is pinned — it is
 * admin-settable via `setVesting`, so a rotation breaks this leg and
 * the fetcher falls through to the API.
 *
 * Fallback 1 is Apyx's rewards API (percent APY, compounded monthly per
 * the RateView natspec). The discover endpoint is season-scoped but old
 * seasons keep serving (season 1 still responds), so pinning season 2
 * stays valid after season 3 starts. Fallback 2 is DefiLlama's
 * first-party pool — whose `apy` field carries the RateView **APR**
 * (14.00136 vs the 13.9917 computed on-chain the same day, while the
 * true APY was 14.94), so it is passed through without APY→APR
 * conversion.
 */

const CHAIN_ID = Chain.ETHEREUM_MAINNET

/** apyUSD — ERC-4626 over apxUSD (Ethereum; the Base/BNB deployments
 *  are bridged mirrors whose 4626 surface reverts, and Solana is
 *  non-EVM). */
const APYUSD: Address = '0x38eeb52f0771140d10c4e9a9a72349a329fe8a6a'
/** LinearVestV0 — read from `apyUSD.vesting()` 2026-08-04. */
const APYX_LINEAR_VEST: Address = '0x0d62b4cc02b4b51ed19ddf41d7a7979cf394c99f'

const APYX_DISCOVER_URL = 'https://api.apyx.fi/v1/rewards/seasons/2/discover'
const APYX_DEFILLAMA_POOL = 'cb6139f9-4a68-4efd-8245-0312a92aee55'

const YEAR_SECONDS = 31_536_000n
const ONE_E18 = 10n ** 18n

export const APYUSD_KEY = 'APYUSD'
/** Canonical token-list assetGroup (Base/BNB form; chain-1 aliased to it). */
export const APYUSD_GROUP_KEY = 'apyUSD::APYUSD'
/** Pre-unification chain-1 stored form — keep emitting until the list
 *  republishes. */
const APYUSD_LEGACY_GROUP_KEY = 'apyUSD::apyUSD'

const APYX_READ_ABI = [
  {
    name: 'totalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'unvestedAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'vestingPeriodRemaining',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

const fetchApyusdAprOnChain = async (): Promise<number> => {
  const [totalAssets, unvested, periodRemaining] =
    (await multicallRetryUniversal({
      chain: CHAIN_ID,
      abi: APYX_READ_ABI,
      calls: [
        { address: APYUSD, name: 'totalAssets', params: [] },
        { address: APYX_LINEAR_VEST, name: 'unvestedAmount', params: [] },
        {
          address: APYX_LINEAR_VEST,
          name: 'vestingPeriodRemaining',
          params: [],
        },
      ],
      allowFailure: false,
    })) as [bigint, bigint, bigint]

  if (totalAssets === 0n || periodRemaining === 0n || unvested === 0n) {
    // 0 here usually means a vesting cycle lapsed before the next
    // deposit (or a rotated vesting contract) — defer to the API
    // rather than reporting a dead rate.
    throw new Error('apyx vesting state empty')
  }
  const perSecond = unvested / periodRemaining
  return Number((perSecond * YEAR_SECONDS * ONE_E18) / totalAssets) / 1e16
}

/** Percent APY from the rewards discover endpoint's `Hold apyUSD` row. */
const fetchApyusdApyFromApi = async (): Promise<number> => {
  const res = await fetch(APYX_DISCOVER_URL, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const items: any[] = (await res.json())?.data?.items ?? []
  const row = items.find(
    (it) =>
      it?.protocol === 'Apyx' &&
      it?.network === 'ethereum' &&
      Array.isArray(it?.assetSymbols) &&
      it.assetSymbols.includes('apyUSD') &&
      it?.apy != null,
  )
  const apy = Number(row?.apy)
  if (Number.isFinite(apy) && apy > 0) return apy
  throw new Error('apyUSD apy missing')
}

export const apyxFetcher: YieldFetcher = {
  label: 'APYUSD',
  fetch: async () => {
    const apr = await fetchApyusdAprOnChain()
      .catch(async () => apyToAprPercent(await fetchApyusdApyFromApi()))
      // DefiLlama's apyx-protocol pool reports the RateView APR in its
      // `apy` field (see header) — pass through unconverted.
      .catch(() => fetchDefiLlamaApy(APYX_DEFILLAMA_POOL))
      .catch(() => 0)
    return {
      [APYUSD_KEY]: apr,
      [APYUSD_GROUP_KEY]: apr,
      [APYUSD_LEGACY_GROUP_KEY]: apr,
    }
  },
}
